import { useEffect, useMemo, useState } from "react";

interface FairOption {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: "DRAFT" | "OPEN" | "CLOSED";
}

interface OverviewResponse {
  events: FairOption[];
}

interface ProductAnalysisItem {
  productId: string;
  agoraId: string;
  barcode: string | null;
  name: string;
  family: string | null;
  minimumUnitPriceCents: number;
  maximumUnitPriceCents: number;
  soldQuantity: number;
  cancelledQuantity: number;
  netQuantity: number;
  ticketCount: number;
  cashCents: number;
  cardCents: number;
  otherCents: number;
  netCents: number;
}

interface ProductAnalysisResponse {
  event: FairOption;
  generatedAt: string;
  summary: {
    distinctProducts: number;
    netUnits: number;
    completedTickets: number;
    cancelledTickets: number;
    totalCents: number;
    cashCents: number;
    cardCents: number;
    otherCents: number;
  };
  items: ProductAnalysisItem[];
}

type SortMode = "units" | "amount" | "name";

const money = new Intl.NumberFormat("ca-ES", {
  style: "currency",
  currency: "EUR",
});
const time = new Intl.DateTimeFormat("ca-ES", {
  hour: "2-digit",
  minute: "2-digit",
});

async function request<T>(url: string): Promise<T> {
  const response = await fetch(url, { credentials: "include" });
  const data = (await response.json().catch(() => null)) as
    | T
    | { message?: string; error?: string }
    | null;
  if (!response.ok) {
    const error = data as { message?: string; error?: string } | null;
    throw new Error(error?.message ?? error?.error ?? "No s'ha pogut carregar l'anàlisi.");
  }
  return data as T;
}

function statusLabel(status: FairOption["status"]) {
  if (status === "OPEN") return "Oberta";
  if (status === "CLOSED") return "Tancada";
  return "Esborrany";
}

function priceLabel(item: ProductAnalysisItem) {
  if (item.minimumUnitPriceCents === item.maximumUnitPriceCents) {
    return money.format(item.minimumUnitPriceCents / 100);
  }
  return `${money.format(item.minimumUnitPriceCents / 100)}–${money.format(item.maximumUnitPriceCents / 100)}`;
}

export function ProductAnalysisPanel() {
  const [events, setEvents] = useState<FairOption[]>([]);
  const [eventId, setEventId] = useState("");
  const [analysis, setAnalysis] = useState<ProductAnalysisResponse | null>(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>("units");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function loadAnalysis(selectedEventId = eventId, quiet = false) {
    if (!selectedEventId) {
      setAnalysis(null);
      setLoading(false);
      return;
    }
    if (!quiet) setLoading(true);
    setError("");
    try {
      setAnalysis(
        await request<ProductAnalysisResponse>(
          `/api/v1/analytics/products?eventId=${encodeURIComponent(selectedEventId)}`,
        ),
      );
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "No s'ha pogut carregar l'anàlisi.",
      );
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    void request<OverviewResponse>("/api/v1/operations/overview")
      .then((overview) => {
        if (cancelled) return;
        setEvents(overview.events);
        setEventId(
          overview.events.find((event) => event.status === "OPEN")?.id ??
            overview.events[0]?.id ??
            "",
        );
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(
          loadError instanceof Error
            ? loadError.message
            : "No s'han pogut carregar les fires.",
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!eventId) return;
    void loadAnalysis(eventId);
    const interval = window.setInterval(() => {
      void loadAnalysis(eventId, true);
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [eventId]);

  const visibleItems = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("ca-ES");
    const filtered = normalizedQuery
      ? (analysis?.items ?? []).filter((item) =>
          [item.name, item.agoraId, item.barcode ?? "", item.family ?? ""]
            .join(" ")
            .toLocaleLowerCase("ca-ES")
            .includes(normalizedQuery),
        )
      : [...(analysis?.items ?? [])];
    return filtered.sort((left, right) => {
      if (sort === "name") return left.name.localeCompare(right.name, "ca");
      if (sort === "amount") return right.netCents - left.netCents;
      return right.netQuantity - left.netQuantity || right.netCents - left.netCents;
    });
  }, [analysis, query, sort]);

  return (
    <div className="analysis-stack">
      <section className="panel analysis-scope">
        <div>
          <p className="eyebrow">FIRA ANALITZADA</p>
          <h2>{analysis?.event.name ?? "Selecciona una fira"}</h2>
          <p>
            Les vendes pendents apareixeran automàticament després de sincronitzar els terminals.
          </p>
        </div>
        <label>
          Fira
          <select
            value={eventId}
            onChange={(event) => setEventId(event.target.value)}
            disabled={events.length === 0}
          >
            {events.length === 0 && <option value="">No hi ha fires disponibles</option>}
            {events.map((event) => (
              <option value={event.id} key={event.id}>
                {statusLabel(event.status)} · {event.name} · {event.startDate}–{event.endDate}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className="analysis-summary">
        <article>
          <span>Total venut</span>
          <strong>{money.format((analysis?.summary.totalCents ?? 0) / 100)}</strong>
          <small>
            {money.format((analysis?.summary.cashCents ?? 0) / 100)} efectiu · {money.format((analysis?.summary.cardCents ?? 0) / 100)} targeta
          </small>
        </article>
        <article>
          <span>Unitats netes</span>
          <strong>{analysis?.summary.netUnits ?? 0}</strong>
          <small>descomptant anul·lacions</small>
        </article>
        <article>
          <span>Productes diferents</span>
          <strong>{analysis?.summary.distinctProducts ?? 0}</strong>
          <small>amb quantitat neta positiva</small>
        </article>
        <article>
          <span>Tickets vàlids</span>
          <strong>{analysis?.summary.completedTickets ?? 0}</strong>
          <small>{analysis?.summary.cancelledTickets ?? 0} anul·lats</small>
        </article>
      </section>

      {error && <p className="alert error" role="alert">{error}</p>}

      <section className="panel analysis-panel">
        <div className="panel-heading analysis-heading">
          <div>
            <p className="eyebrow">PRODUCTES VENUTS</p>
            <h2>Detall agrupat</h2>
            <small>
              {analysis?.generatedAt
                ? `Actualitzat a les ${time.format(new Date(analysis.generatedAt))}`
                : "Esperant dades"}
            </small>
          </div>
          <button
            className="secondary"
            type="button"
            disabled={loading || !eventId}
            onClick={() => void loadAnalysis()}
          >
            {loading ? "Actualitzant…" : "Actualitzar"}
          </button>
        </div>

        <div className="analysis-filters">
          <label>
            Cerca producte
            <input
              type="search"
              placeholder="Nom, ID Àgora, codi o família"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <label>
            Ordena per
            <select value={sort} onChange={(event) => setSort(event.target.value as SortMode)}>
              <option value="units">Més unitats</option>
              <option value="amount">Més import</option>
              <option value="name">Nom del producte</option>
            </select>
          </label>
          <span className="badge">{visibleItems.length} productes</span>
        </div>

        <div className="analysis-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Producte</th>
                <th>Tickets</th>
                <th>Venudes</th>
                <th>Anul·lades</th>
                <th>Netes</th>
                <th>Import net</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item) => (
                <tr key={item.productId}>
                  <td>
                    <strong>{item.name}</strong>
                    <small>{item.family ?? "Sense família"} · ID Àgora {item.agoraId}</small>
                    <small>{item.barcode ? `Codi ${item.barcode}` : "Sense codi"} · {priceLabel(item)}</small>
                  </td>
                  <td>{item.ticketCount}</td>
                  <td>{item.soldQuantity}</td>
                  <td className={item.cancelledQuantity > 0 ? "analysis-cancelled" : ""}>
                    {item.cancelledQuantity}
                  </td>
                  <td><strong>{item.netQuantity}</strong></td>
                  <td className="analysis-amount">
                    <strong>{money.format(item.netCents / 100)}</strong>
                    <small>{money.format(item.cashCents / 100)} efectiu</small>
                    <small>{money.format(item.cardCents / 100)} targeta</small>
                  </td>
                </tr>
              ))}
              {!loading && visibleItems.length === 0 && (
                <tr>
                  <td className="catalog-empty" colSpan={6}>
                    Encara no hi ha productes venuts amb aquest filtre.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
